import Icon from "../../NGMaterialIcon/NGMaterialIcon";
import { Box, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from "@mui/material";
import { useSignal } from "@preact/signals-react";
import { useRef } from "react";
import DimensionField from "../NGDimensionEditor";
import { NGSpacingInput as SpacingInput } from "../NGSpacingInput/NGSpacingInput";
import NGDimensionPositionStylesEditor from "./NGDimensionPositionStylesEditor";

function getCssValues(property = "") {
  // Split the value by spaces
  const values = property.split(/\s+/);

  // Handle the values based on how many there are
  switch (values.length) {
    case 1:
      return [values[0], values[0], values[0], values[0]]; // All sides are the same
    case 2:
      return [values[0], values[1], values[0], values[1]]; // Top/Bottom, Left/Right
    case 3:
      return [values[0], values[1], values[2], values[1]]; // Top, Left/Right, Bottom
    case 4:
      return values; // Top, Right, Bottom, Left
    default:
      return []; // Unexpected case
  }
}

export default function NGCommonStylesEditor({ selected, layout }) {
  const borderSideButton = useSignal<HTMLElement | null>(null);
  const borderSideMenuOpen = Boolean(borderSideButton.value);
  const inputBorderWidthRef = useRef<HTMLInputElement>(null);

  const sides = ["borderTop", "borderRight", "borderBottom", "borderLeft", "border"];

  const {
    borderTop,
    borderBottom,
    borderLeft,
    borderRight,
    border,
    borderColor,
    gap,
    padding = "",
    margin = "",
  } = selected.value;

  const sideSelectedIndex = (() => {
    if (borderTop) return 1;
    if (borderRight) return 2;
    if (borderBottom) return 3;
    if (borderLeft) return 4;
    if (borderColor) return 5;

    return 0;
  })();

  const selectedBorder = (() => {
    if (borderTop) return "borderTop";
    if (borderBottom) return "borderBottom";
    if (borderLeft) return "borderLeft";
    if (borderRight) return "borderRight";
    if (border || borderColor) return "border";
    return "";
  })();

  const handleChange = (value: object) => {
    const c = { ...selected.value };
    const o1 = Object.keys(value)[0];
    const o2 = Object.values(value)[0];
    if (!o2) {
      delete c?.[o1];
    } else {
      try {
        c[o1] = JSON.parse(o2);
      } catch {
        c[o1] = o2;
      }
    }
    selected.value = c;
  };

  const showCustomBorderSides = borderColor;

  const handleBorderSideSelected = (value: string) => {
    /* borderSideSelectedIndex.value = index; */
    const updatedValues = { ...selected.value };

    if (value === "custom") {
      if (selectedBorder !== "border") {
        updatedValues.borderStyle = selected.value[`${selectedBorder}Style`];

        delete updatedValues[selectedBorder];
        delete updatedValues[`${selectedBorder}Width`];
        delete updatedValues[`${selectedBorder}Style`];
      }

      updatedValues.borderColor = selected.value[selectedBorder];
      updatedValues.borderTopWidth = selected.value[`${selectedBorder}Width`];
      updatedValues.borderRightWidth = selected.value[`${selectedBorder}Width`];
      updatedValues.borderBottomWidth = selected.value[`${selectedBorder}Width`];
      updatedValues.borderLeftWidth = selected.value[`${selectedBorder}Width`];
    } else {
      sides.forEach((side) => {
        delete updatedValues[side];
        delete updatedValues[`${side}Width`];
        delete updatedValues[`${side}Style`];
      });
      delete updatedValues.borderColor;

      const widthValue = showCustomBorderSides
        ? inputBorderWidthRef?.current?.value
        : selected.value[`${selectedBorder}Width`] ?? "1px";

      updatedValues[value] = selected.value[selectedBorder] ?? selected.value.borderColor;
      updatedValues[`${value}Style`] = selected.value[`${selectedBorder}Style`];
      updatedValues[`${value}Width`] = widthValue;
    }

    selected.value = updatedValues;
    handleCloseBorderSideMenu();
  };

  const handleCloseBorderSideMenu = () => {
    borderSideButton.value = null;
  };

  const borderSides = [
    { Icon: <Icon name="border_outer" />, Text: "All", Value: "border" },
    { Icon: <Icon name="border_top" />, Text: "Top", Value: "borderTop" },
    { Icon: <Icon name="border_right" />, Text: "Right", Value: "borderRight" },
    { Icon: <Icon name="border_bottom" />, Text: "Bottom", Value: "borderBottom" },
    { Icon: <Icon name="border_left" />, Text: "Left", Value: "borderLeft" },
    { Icon: <Icon name="tune" />, Text: "Custom", Value: "custom" },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "6px" }}>
      <NGDimensionPositionStylesEditor selected={selected} layout={layout} />
      <Stack direction="row" justifyContent="space-between" gap="1rem">
        <DimensionField
          label={<Icon name="space_bar" fontSize="small" className="label-color" />}
          title="Gap"
          field={"gap"}
          value={String(gap || "")}
          valueType={"text"}
          onValueChange={(e) => handleChange({ gap: (e.target as HTMLInputElement).value })}
          styles={{ margin: 0 }}
        />
        <Box flex={1} />
      </Stack>

      <Stack direction="row" justifyContent="space-between" gap="1rem">
        <SpacingInput label="M" title="Margin" value={margin} onChange={(margin) => handleChange({ margin })} />
        <SpacingInput label="P" title="Padding" value={padding} onChange={(padding) => handleChange({ padding })} />
      </Stack>

      <Menu
        id="border-side-menu"
        anchorEl={borderSideButton.value}
        open={borderSideMenuOpen}
        onClose={handleCloseBorderSideMenu}
        MenuListProps={{
          "aria-labelledby": "lock-button",
          role: "listbox",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ marginTop: "10px", marginLeft: "10px" }}
      >
        {borderSides.map((side, index) => (
          <MenuItem
            key={index}
            disabled={index === sideSelectedIndex}
            selected={index === sideSelectedIndex}
            onClick={() => handleBorderSideSelected(side.Value)}
          >
            <ListItemIcon>{side.Icon}</ListItemIcon>
            <ListItemText>{side.Text}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
